// Web Audio API synth - warm sine/triangle tone with ADSR envelope.
import { pitchToFreq } from "./music";
import { soundNotice, beatPulse, getVolume, on, prettyPitch } from "./a11yBus";

let ctx = null;

function getCtx() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

let masterGain = null;
function master(ac) {
  if (!masterGain) {
    masterGain = ac.createGain();
    masterGain.gain.value = getVolume();
    masterGain.connect(ac.destination);
    on("volume", (v) => { masterGain.gain.value = v; });
  }
  return masterGain;
}

export const audioClock = () => getCtx();
export const audioOut = () => master(getCtx());
export const outputLatencySec = () => { const ac = getCtx(); return (ac.outputLatency || 0) + (ac.baseLatency || 0); };

// Instant-attack percussive click; `when` is an AudioContext time (defaults to now).
export function playTick(freq = 1000, duration = 0.05, vol = 1, when) {
  const ac = getCtx();
  const t = Math.max(when ?? ac.currentTime, ac.currentTime);
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "triangle";
  osc.frequency.value = freq;
  osc.connect(gain);
  gain.connect(master(ac));
  gain.gain.setValueAtTime(0.5 * vol, t);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
  osc.start(t);
  osc.stop(t + duration + 0.02);
}

export function playFreq(freq, duration = 1.1, vol = 1) {
  if (!freq) return null;
  const ac = getCtx();
  const now = ac.currentTime;

  const osc = ac.createOscillator();
  const osc2 = ac.createOscillator();
  const gain = ac.createGain();

  osc.type = "sine";
  osc2.type = "triangle";
  osc.frequency.value = freq;
  osc2.frequency.value = freq;
  osc2.detune.value = 4;

  // Warm low-pass to soften the tone
  const filter = ac.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 2400;

  osc.connect(filter);
  osc2.connect(filter);
  filter.connect(gain);
  gain.connect(master(ac));

  // ADSR
  const peak = Math.max(0.0002, 0.28 * vol);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(peak * 0.7, now + 0.12);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  osc.start(now);
  osc2.start(now);
  osc.stop(now + duration + 0.05);
  osc2.stop(now + duration + 0.05);
  return () => {
    const t = ac.currentTime;
    gain.gain.cancelScheduledValues(t);
    gain.gain.setValueAtTime(0.0001, t);
    try { osc.stop(t + 0.01); osc2.stop(t + 0.01); } catch (e) {}
  };
}

export function playPitch(pitch, duration = 1.1) {
  soundNotice(`Playing note ${prettyPitch(pitch)}`);
  playFreq(pitchToFreq(pitch), duration);
}

// Play a sequence of pitches in order (for scales / melodies).
let seqTimers = [];
let seqVoices = [];
let seqStep = null;

export function stopSequence() {
  seqTimers.forEach(clearTimeout);
  seqVoices.forEach((stop) => stop && stop());
  seqTimers = [];
  seqVoices = [];
  if (seqStep) seqStep(-1);
  seqStep = null;
}

// Play a sequence of pitches in order; pressing again restarts cleanly from the start.
export function playSequence(pitches, gap = 0.55, duration = 0.5, onStep = null) {
  stopSequence();
  seqStep = onStep;
  soundNotice(`Playing: ${pitches.map(prettyPitch).join(" ")}`);
  pitches.forEach((p, i) => {
    seqTimers.push(setTimeout(() => {
      seqVoices.push(playFreq(pitchToFreq(p), duration));
      if (onStep) onStep(i);
    }, i * gap * 1000));
  });
  seqTimers.push(setTimeout(() => { if (onStep) onStep(-1); seqStep = null; }, pitches.length * gap * 1000 + 100));
}

export function initAudio() {
  getCtx();
}

// ---------------------------------------------------------------------------
// Metronome — short click bursts scheduled on a steady interval.
// ---------------------------------------------------------------------------
let metroTimer = null;

// type: "down" (accented downbeat) | "beat" | "sub" (subdivision) | "wood" (count-in)
function playClick(type) {
  const ac = getCtx();
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  const cfg = {
    down: { osc: "square", freq: 1600, peak: 0.36, dur: 0.05 },
    beat: { osc: "square", freq: 1100, peak: 0.24, dur: 0.05 },
    sub: { osc: "square", freq: 720, peak: 0.11, dur: 0.05 },
    wood: { osc: "triangle", freq: 2100, peak: 0.5, dur: 0.028 },
  }[type] || { osc: "square", freq: 1100, peak: 0.24, dur: 0.05 };
  osc.type = cfg.osc;
  osc.frequency.value = cfg.freq;
  gain.gain.setValueAtTime(cfg.peak, now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + cfg.dur);
  osc.connect(gain);
  gain.connect(master(ac));
  osc.start(now);
  osc.stop(now + cfg.dur + 0.01);
}

// subdivision: 1 = quarter, 2 = eighth, 3 = triplet notes per beat.
// onBeat(beatNum, inCountIn) fires on each main beat. The first `countInBeats`
// beats play a distinct woodblock click as a count-in.
export function startMetronome(bpm = 80, beatsPerBar = 4, subdivision = 1, onBeat = null, countInBeats = 0) {
  stopMetronome();
  getCtx();
  soundNotice(`Metronome on · ${bpm} bpm`);
  const sub = Math.max(1, subdivision);
  const intervalMs = ((60 / bpm) / sub) * 1000;
  const totalTicks = beatsPerBar * sub;
  let tick = 0;
  let beatsPlayed = 0;
  const fire = () => {
    const isBeat = tick % sub === 0;
    const beatNum = Math.floor(tick / sub);
    if (isBeat) {
      const inCountIn = beatsPlayed < countInBeats;
      if (inCountIn) playClick("wood");
      else playClick(beatNum === 0 ? "down" : "beat");
      if (onBeat) onBeat(beatNum, inCountIn);
      beatPulse(beatNum === 0);
      beatsPlayed += 1;
    } else if (countInBeats === 0 || beatsPlayed > countInBeats) {
      playClick("sub");
    }
    tick = (tick + 1) % totalTicks;
  };
  fire();
  metroTimer = setInterval(fire, intervalMs);
}

export function stopMetronome() {
  if (metroTimer) {
    clearInterval(metroTimer);
    metroTimer = null;
  }
}

export function isMetronomeRunning() {
  return metroTimer !== null;
}
